import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import Text from '../../atoms/Text';
import styles from './DateInput.module.scss';
import CustomTooltipInfo from '../../atoms/CustomTooltipInfo';

export interface Props {
  /** A YYYY-MM-DD ISO date string. */
  value?: string;
  id?: string;
  name?: string;
  className?: string;
  tooltip?: string;
  label?: string;
  disabled?: boolean;
  helperText?: string;
  errorText?: string;
  style?: React.CSSProperties;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
}

const DateInput: React.FC<Props> = ({
  id,
  name = 'date',
  value,
  helperText,
  errorText,
  className,
  disabled,
  tooltip,
  label,
  style,
  onChange,
  onBlur,
}) => {
  const [day, setDay] = useState(
    value?.split('-')?.[2] ? value.split('-')[2].padStart(2, '0') : '',
  );
  const [month, setMonth] = useState(
    value?.split('-')?.[1] ? value.split('-')[1].padStart(2, '0') : '',
  );
  const [year, setYear] = useState(
    value?.split('-')?.[0] ? value.split('-')[0].padStart(2, '0') : '',
  );

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  /** Sync internal states with external state */
  React.useEffect(() => {
    if (value && moment(value).isValid()) {
      setDay(value.split('-')[2]);
      setMonth(value.split('-')[1]);
      setYear(value.split('-')[0]);
    }
  }, [value]);

  const handleChange = (
    part: string,
    val: string,
    maxLen: number,
    nextRef?: React.RefObject<HTMLInputElement>,
  ) => {
    let numericVal = val.replace(/\D/g, '').slice(0, maxLen);

    if (numericVal.length === maxLen) {
      let num = Number.parseInt(numericVal, 10);

      if (part === 'month') {
        if (num < 1) num = 1;
        if (num > 12) num = 12;
        numericVal = num.toString().padStart(2, '0');
      }

      if (part === 'day') {
        if (num < 1) num = 1;
        if (num > 31) num = 31;
        numericVal = num.toString().padStart(2, '0');
      }
    }

    setDay((prev) => (part === 'day' ? numericVal : prev));
    setMonth((prev) => (part === 'month' ? numericVal : prev));
    setYear((prev) => (part === 'year' ? numericVal : prev));

    /** Update controlled value */
    if (part === 'day' && month && year) {
      onChange?.(
        `${year?.toString()}-${month
          .toString()
          .padStart(2, '0')}-${numericVal}`,
      );
    } else if (part === 'month' && day && year) {
      onChange?.(
        `${year.toString()}-${numericVal}-${day.toString().padStart(2, '0')}`,
      );
    } else if (part === 'year' && month && day) {
      onChange?.(
        `${numericVal}-${month.toString().padStart(2, '0')}-${day
          .toString()
          .padStart(2, '0')}`,
      );
    } else {
      onChange?.('');
    }

    /** Move cursor to next date segment */
    if (numericVal.length === maxLen && nextRef) {
      nextRef.current?.focus();
      nextRef.current?.select();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    prevRef?: React.RefObject<HTMLInputElement>,
    nextRef?: React.RefObject<HTMLInputElement>,
  ) => {
    if (e.key === 'ArrowLeft' && prevRef) {
      e.preventDefault();
      prevRef.current?.focus();
      prevRef.current?.select();
    }

    if (e.key === 'ArrowRight' && nextRef) {
      e.preventDefault();
      nextRef.current?.focus();
      nextRef.current?.select();
    }
  };

  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
      onBlur?.(value || '');
    }
  };

  const cls = classNames(styles.wrapper, className, 'd-flex flex-column', {
    error: !!errorText,
    disabled,
  });

  const inputId = id ?? uuid();

  return (
    <div className={cls} style={style}>
      {label && (
        <div className="d-flex gap-1 text-items-center mb-2">
          <Text as="label" htmlFor={inputId} weight="bold">
            {label}
          </Text>
          {tooltip && <CustomTooltipInfo info={tooltip} />}
        </div>
      )}
      <input id={inputId} type="hidden" value={value} name={name} />

      <div
        className={classNames(styles.container, {
          error: !!errorText,
          disabled,
        })}
        role="button"
        tabIndex={0}
        onClick={(e) => {
          if (e.target instanceof HTMLInputElement) {
            return;
          }
          dayRef.current?.focus();
          dayRef.current?.select();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            dayRef.current?.focus();
            dayRef.current?.select();
          }
        }}
        onBlur={handleContainerBlur}
      >
        <input
          ref={dayRef}
          className={classNames(styles.input, { error: !!errorText })}
          value={day}
          onChange={(e) => handleChange('day', e.target.value, 2, monthRef)}
          onFocus={(e) => e.target.select()}
          placeholder="DD"
          onKeyDown={(e) => handleKeyDown(e, undefined, monthRef)}
          disabled={disabled}
        />
        <span>/</span>
        <input
          ref={monthRef}
          className={classNames(styles.input, { error: !!errorText })}
          value={month}
          onChange={(e) => handleChange('month', e.target.value, 2, yearRef)}
          onFocus={(e) => e.target.select()}
          placeholder="MM"
          onKeyDown={(e) => handleKeyDown(e, dayRef, yearRef)}
          disabled={disabled}
        />
        <span>/</span>
        <input
          ref={yearRef}
          className={classNames(styles.input, { error: !!errorText })}
          value={year}
          onChange={(e) => handleChange('year', e.target.value, 4)}
          onFocus={(e) => e.target.select()}
          style={{ width: '52px' }}
          placeholder="AAAA"
          onKeyDown={(e) => handleKeyDown(e, monthRef)}
          disabled={disabled}
        />
      </div>

      {helperText && !errorText && (
        <Text as="span" className="mt-2" size="sm1">
          {helperText}
        </Text>
      )}
      {errorText && (
        <Text as="span" className="mt-2" color="red400" size="sm1">
          {errorText}
        </Text>
      )}
    </div>
  );
};

export default DateInput;
